import { z } from 'zod';
import {
  Uuid,
  Email,
  Iso,
  Cents,
  DiscoveryRunStatus,
  DiscoveryTrigger,
  KeepaSearchRunStatus,
  KeepaSearchRunTrigger,
  KeepaTierInferred,
  Provider,
  RoiGrade,
  Plan,
  ProductStatus,
  Role,
  SupplierCandidateStatus,
  SupplierCandidateTier,
  SupplierPipelineStatus,
  ValidationStatus,
} from './primitives';
import { BrandSizeStatus } from './brand';

export const User = z.object({
  id: Uuid,
  tenantId: Uuid,
  email: Email,
  name: z.string().nullable(),
  role: Role,
  createdAt: Iso,
  /** 'va'-role permission grants; empty for every other role. Defaulted so
   *  a web bundle deployed ahead of the api Worker (which wouldn't send
   *  this field yet) still parses — same skew-tolerance shape as
   *  MeResponse.modules. */
  permissions: z.array(z.string()).default([]),
});
export type User = z.infer<typeof User>;

export const Tenant = z.object({
  id: Uuid,
  name: z.string(),
  slug: z.string(),
  plan: Plan,
  createdAt: Iso,
});
export type Tenant = z.infer<typeof Tenant>;

export const CredentialMeta = z.object({
  id: Uuid,
  provider: Provider,
  label: z.string().nullable(),
  validationStatus: ValidationStatus,
  lastValidatedAt: Iso.nullable(),
  // Provider-specific public metadata (never the secret itself):
  //  - openrouter: { model: 'anthropic/claude-haiku-4.5' }
  //  - keepa:      { keepaTier: 'basic_19eur' }
  metadata: z.record(z.unknown()).nullable(),
  createdAt: Iso,
});
export type CredentialMeta = z.infer<typeof CredentialMeta>;

export const RoiThresholds = z
  .object({
    A: z.number().default(0.5),
    B: z.number().default(0.3),
    C: z.number().default(0.15),
    D: z.number().default(0.05),
    F: z.number().default(0),
  })
  .default({});
export type RoiThresholds = z.infer<typeof RoiThresholds>;

export const FeeProfileData = z.object({
  defaultReferralPct: z.number().min(0).max(1),
  defaultFbaPickPack: z.number().nonnegative(),
  defaultStoragePerUnit: z.number().nonnegative(),
  defaultPrepPerUnit: z.number().nonnegative(),
  defaultFreightPctOfLanded: z.number().nonnegative(),
  // When supplier cost isn't known (typical for fresh Keepa results), assume
  // supplier-cost = sale-price × this markup. Pre-existing profile JSON without
  // this field reads back as 0.5 thanks to .default().
  defaultSupplierMarkupPct: z.number().min(0).max(1).default(0.5),
  categoryOverrides: z
    .record(
      z.object({
        referralPct: z.number().min(0).max(1).optional(),
        pickPack: z.number().nonnegative().optional(),
      })
    )
    .default({}),
  roiThresholds: RoiThresholds,
});
export type FeeProfileData = z.infer<typeof FeeProfileData>;

export const FeeProfile = z.object({
  id: Uuid,
  name: z.string(),
  isDefault: z.boolean(),
  data: FeeProfileData,
  version: z.number().int(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type FeeProfile = z.infer<typeof FeeProfile>;

// ─────────────────────────────────────────────────────────
// Suppliers (Phase 1.5)
// ─────────────────────────────────────────────────────────

export const SupplierAsin = z.object({
  asin: z.string().length(10),
  pinnedAt: Iso,
  notes: z.string().nullable(),
});
export type SupplierAsin = z.infer<typeof SupplierAsin>;

export const Supplier = z.object({
  id: Uuid,
  name: z.string(),
  website: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryContactName: z.string().nullable(),
  status: SupplierPipelineStatus,
  notes: z.string().nullable(),
  pinnedAsinCount: z.number().int().nonnegative(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type Supplier = z.infer<typeof Supplier>;

export const SupplierDetail = Supplier.omit({ pinnedAsinCount: true }).extend({
  asins: z.array(SupplierAsin),
});
export type SupplierDetail = z.infer<typeof SupplierDetail>;

// ─────────────────────────────────────────────────────────
// Discovery (Phase 1.5 Weeks 13–14)
// ─────────────────────────────────────────────────────────

export const DiscoveryRun = z.object({
  id: Uuid,
  asin: z.string().length(10),
  trigger: DiscoveryTrigger,
  status: DiscoveryRunStatus,
  startedAt: Iso,
  completedAt: Iso.nullable(),
  tavilyCalls: z.number().int().nonnegative(),
  tavilyCreditsUsed: z.number().int().nonnegative(),
  llmCalls: z.number().int().nonnegative(),
  llmInputTokens: z.number().int().nonnegative(),
  llmOutputTokens: z.number().int().nonnegative(),
  candidateCount: z.number().int().nonnegative(),
  errorMessage: z.string().nullable(),
});
export type DiscoveryRun = z.infer<typeof DiscoveryRun>;

export const SupplierCandidate = z.object({
  id: Uuid,
  asin: z.string().length(10),
  discoveryRunId: Uuid,
  tier: SupplierCandidateTier,
  companyName: z.string(),
  website: z.string().nullable(),
  contactEmail: z.string().nullable(),
  sourceUrl: z.string(),
  rationale: z.string(),
  status: SupplierCandidateStatus,
  promotedSupplierId: Uuid.nullable(),
  createdAt: Iso,
});
export type SupplierCandidate = z.infer<typeof SupplierCandidate>;

// ─────────────────────────────────────────────────────────
// Keepa query templates (Phase 1.8 Pass 1)
// ─────────────────────────────────────────────────────────

/**
 * A single user-swappable slot in a query template.
 *
 * `path` is a dot-separated path into KeepaFilters (e.g. "category" or
 * "amazonPriceCurrent.max"). The runtime resolver writes parameterValues into
 * a clone of fixedFilters at those paths before sending to Keepa.
 *
 * `kind` controls the SPA form renderer:
 *   string  → text input
 *   cents   → currency input ($ → cents conversion in the SPA)
 *   int     → numeric input
 *   enum    → select; requires `options`
 *   strings → comma-separated text input → string[] (e.g. brand multi-value)
 */
export const TemplateParameterKind = z.enum(['string', 'cents', 'int', 'enum', 'strings']);
export type TemplateParameterKind = z.infer<typeof TemplateParameterKind>;

export const TemplateParameterSpec = z
  .object({
    path: z.string().min(1).max(80),
    label: z.string().min(1).max(80),
    kind: TemplateParameterKind,
    required: z.boolean().default(false),
    defaultValue: z.union([z.string(), z.number(), z.array(z.string()), z.null()]).optional(),
    options: z.array(z.string()).optional(),
    helpText: z.string().max(200).optional(),
  })
  .strict();
export type TemplateParameterSpec = z.infer<typeof TemplateParameterSpec>;

export const TemplateOrigin = z.enum(['builtin', 'custom']);
export type TemplateOrigin = z.infer<typeof TemplateOrigin>;

/**
 * A keepa_search_runs row — the per-execution state machine + telemetry
 * snapshot. The run owns the resolved filter set + fee-profile snapshot, so
 * the products it produces stay deterministic even if the template or
 * profile is later edited.
 */
export const KeepaSearchRun = z.object({
  id: Uuid,
  templateId: Uuid.nullable(),
  scheduleId: Uuid.nullable(),
  trigger: KeepaSearchRunTrigger,
  status: KeepaSearchRunStatus,
  /** Final KeepaFilters JSON sent to the adapter (parameters merged in). */
  resolvedFilters: z.record(z.unknown()),
  /** Conservative upper bound from estimateKeepaCost at queue time. */
  estimatedKeepaTokens: z.number().int().nonnegative(),
  /** Fee-profile snapshot so the run's scoring stays deterministic. */
  feeProfileId: Uuid.nullable(),
  feeProfileVersion: z.number().int().nullable(),
  queuedAt: Iso.nullable(),
  startedAt: Iso.nullable(),
  completedAt: Iso.nullable(),
  nextEligibleAt: Iso.nullable(),
  deferCount: z.number().int().nonnegative(),
  keepaTokensConsumed: z.number().int().nullable(),
  keepaTokensLeftAfter: z.number().int().nullable(),
  resultCount: z.number().int().nonnegative(),
  newProductCount: z.number().int().nonnegative(),
  keepaTotalResults: z.number().int().nonnegative(),
  errorMessage: z.string().nullable(),
  createdAt: Iso,
});
export type KeepaSearchRun = z.infer<typeof KeepaSearchRun>;

/** Snapshot of a keepa_token_ledger row — exposed for the SPA's debug view. */
export const KeepaTokenLedgerEntry = z.object({
  id: Uuid,
  searchRunId: Uuid.nullable(),
  // Null = an attempted call whose spend Keepa didn't report — UNKNOWN,
  // never coerced to 0 (2026-09-17 provider-queues Task 3 honest-spend fix).
  tokensConsumed: z.number().int().nonnegative().nullable(),
  keepaTokensLeftAfter: z.number().int().nullable(),
  inferredTier: KeepaTierInferred,
  occurredAt: Iso,
});
export type KeepaTokenLedgerEntry = z.infer<typeof KeepaTokenLedgerEntry>;

export const KeepaQueryTemplate = z.object({
  id: Uuid,
  name: z.string().min(1).max(120),
  description: z.string().nullable(),
  /**
   * The original natural-language query the operator typed. Nullable because
   * templates created via the TemplatesPage raw-JSON editor have no NL source —
   * only the SaveQueryDialog flow (post-preview / post-results on SearchPage)
   * populates it. The SearchPage and TemplatesPage surface this as
   * "Original query" so users see why the template was made.
   */
  naturalLanguageQuery: z.string().nullable(),
  /**
   * Partial<KeepaFilters> — validated when the user runs the template
   * (parameter values are merged in first, then the result is parsed against
   * the full KeepaFilters schema). Stored as JSONB so partials round-trip.
   */
  fixedFilters: z.record(z.unknown()),
  parameters: z.array(TemplateParameterSpec),
  origin: TemplateOrigin,
  /** Stable identifier for built-in templates; null for user-created rows. */
  builtinSlug: z.string().nullable(),
  archivedAt: Iso.nullable(),
  lastUsedAt: Iso.nullable(),
  useCount: z.number().int().nonnegative(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type KeepaQueryTemplate = z.infer<typeof KeepaQueryTemplate>;

/**
 * A keepa_query_schedules row — Phase 1.8 Pass 3. Schedules bind a template +
 * frozen parameter values to a cron expression. The sweep fires due rows by
 * inserting a `keepa_search_runs` row with trigger='schedule'.
 *
 * The 30-day reconfirm + monthly cap are the agent-rule guardrails (see
 * CLAUDE.md "no autonomous loops"). reconfirmRequiredAt is what the SPA
 * surfaces in a "your schedule needs confirmation" banner.
 */
/**
 * Single entry in a schedule's discovery sort rotation. Each scheduled fire
 * picks the entry at `sortIndex % sortRotation.length` and overrides the
 * template's `sortBy/sortOrder`. After each fire, `sortIndex` advances; when
 * it wraps to 0, `pageCursor` advances. See lib/keepa-search/cron-sweep.ts.
 */
export const SortRotationEntry = z.object({
  sortBy: z.enum(['bsr', 'price', 'reviewCount', 'monthlySold', 'rating']),
  sortOrder: z.enum(['asc', 'desc']),
});
export type SortRotationEntry = z.infer<typeof SortRotationEntry>;

export const KeepaQuerySchedule = z.object({
  id: Uuid,
  templateId: Uuid,
  name: z.string().min(1).max(120),
  /** Path-keyed substitutions used on every fire (same shape as the resolver). */
  parameterValues: z.record(z.unknown()),
  cronExpression: z.string().min(1).max(64),
  nextFireAt: Iso,
  lastFiredAt: Iso.nullable(),
  enabled: z.boolean(),
  reconfirmRequiredAt: Iso,
  monthlyRunCap: z.number().int().positive(),
  runsThisWindow: z.number().int().nonnegative(),
  windowStartedAt: Iso,
  /** Current Keepa /query page; advances each schedule fire (mod totalPages). */
  pageCursor: z.number().int().nonnegative(),
  /** Consecutive runs with 0 new products; ≥3 → auto-pause (catalog_exhausted). */
  consecutiveEmptyRuns: z.number().int().nonnegative(),
  /** When non-null, each fire picks `sortRotation[sortIndex]` instead of the template's sort. */
  sortRotation: z.array(SortRotationEntry).nullable(),
  /** Index into sortRotation; meaningless when sortRotation is null. */
  sortIndex: z.number().int().nonnegative(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type KeepaQuerySchedule = z.infer<typeof KeepaQuerySchedule>;

export const ScoredProduct = z.object({
  id: Uuid,
  asin: z.string().length(10),
  title: z.string().nullable(),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  currentPriceCents: Cents.nullable(),
  currentBsr: z.number().int().nullable(),
  monthlySalesEstimate: z.number().int().nullable(),
  reviewCount: z.number().int().nullable(),
  rating: z.number().nullable(),
  amazonIsSeller: z.boolean().nullable(),
  estimatedLandedCostCents: Cents.nullable(),
  estimatedRoiPct: z.number().nullable(),
  roiGrade: RoiGrade.nullable(),
  feeProfileVersion: z.number().int().nullable(),
  keepaRefreshedAt: Iso,
  /** Operator workflow state. Defaults to `pending` on first discovery. */
  status: ProductStatus,
  /** Operator-supplied supplier cost; null = use markup default. */
  manualSupplierCostCents: Cents.nullable(),
  notes: z.string().nullable(),
  // ── SP-API enrichment (Phase 1.8 Pass 4) ──────────────────────────────
  // All nullable: only populated when the tenant has connected SP-API and
  // the enrichment ran successfully. Pre-existing rows + tenants without
  // SP-API see them as null and the SPA falls back to fee-profile fees.
  /** Listings Restrictions gating verdict — true when Amazon blocks the seller. */
  gated: z.boolean().nullable(),
  /** Total Amazon fees (referral + FBA + closing) for the live price, in cents. */
  exactFeesCents: Cents.nullable(),
  /** Current Buy Box price (cents) — null when no offers exist. */
  liveBuyBoxCents: Cents.nullable(),
  /** Total competing offers on the listing (Amazon + 3P). */
  competingOfferCount: z.number().int().nonnegative().nullable(),
  /**
   * Brand-size annotation populated by the NL Search route from
   * keepa_brand_cache. `null` = product has no brand OR brand-stats is
   * disabled for this tenant; `'pending'` = a background probe is
   * in-flight (the SPA should re-poll); otherwise the resolved bucket.
   */
  brandSize: BrandSizeStatus.nullable().optional(),
});
export type ScoredProduct = z.infer<typeof ScoredProduct>;
